import { UseVenueEventsService } from "@app-contexts/dashboard/features/events/data/services/events/useVenueEventsService";
import { VenueBasedEventSerializer } from "@app-contexts/dashboard/features/events/data/types/events";
import { VenueSerializer } from "@app-contexts/dashboard/features/venues/data/types/venues";
import { UsePublicEventService } from "@app-contexts/public/features/event-detail/data/services/usePublicEventsService";
import { PublicVenueSerializer } from "@app-contexts/public/features/org-profile/data/types/public-venue";
import Button from "@shared/components/ui-kit/form/Button/Button.web";
import ResponsiveDialog from "@shared/components/ui-kit/overlays/ResponsiveDialog/ResponsiveDialog.web";
import { enums, PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import { useEmbeddedComponent } from "@shared/hooks/useEmbeddedComponent";
import TicketTypesList from "@shared/modules/events/components/tickets/TicketTypesList/TicketTypesList.web";
import {
  PublicBaseEventSerializer,
  TicketTypeSerializer,
} from "@shared/modules/events/types/public";
import {
  isPublicEvent,
  isVenueBasedEvent,
} from "@shared/modules/events/utils/event-utils";
import { getEmbeddedPurchasePlatform } from "@shared/modules/financials/utils/purchase-platform-utils";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import { SeatingItemPropertyMap } from "@shared/modules/seating/types/seating-property-types";
import { BestAvailableSeatingQueryParamsActionType } from "@shared/modules/seating/types/seating-types";

import { useTranslation } from "@shared/utils/internationalization";
import Text from "@ui-kit/typography/Text/Text.web";
import { useRouter } from "next/router";
import { Dispatch } from "react";
import { BestAvailableQueryActions } from "../../hooks/useBestAvailableQueryParams";
import {
  BestAvailableTypeActions,
  useBestAvailableTypeState,
} from "./hooks/useBestAvailableTypeState";

const getEventDataWithVisibleTicketTypes = (
  eventData: VenueBasedEventSerializer | PublicBaseEventSerializer,
  typesOnMap: Array<number>,
  seatingItemArray: SeatingItemPropertyMap[] | undefined
) => {
  if (!eventData) return undefined;

  if (isVenueBasedEvent(eventData) && eventData.tickettype_set) {
    return {
      ...eventData,
      tickettype_set: eventData.tickettype_set
        .filter((tt) => typesOnMap.includes(tt.id))
        .map((tt) => ({
          ...tt,
          purchase_limit: tt.box_office_purchase_limit ?? tt.purchase_limit,
          fees_pricing_info: seatingItemArray?.find((item) => item.id === tt.id)
            ?.fees_pricing_info,
        })),
    };
  }

  if (isPublicEvent(eventData) && eventData.ticket_types) {
    return {
      ...eventData,
      ticket_types: eventData.ticket_types.filter((tt) =>
        typesOnMap.includes(tt.id)
      ),
    };
  }

  return eventData;
};

export function BestAvailableTicketTypeSelectionDialog({
  isOpen,
  onClose,
  updateBestAvailableQueryParams,
  eventsService,
  eventSlug,
  initialSelectedTicketTypes,
  typesOnMap,
  venueData,
}: BestAvailableTicketTypeSelectionDialogProps) {
  const { t } = useTranslation("seating");
  const service = eventsService();
  const useGetWithSlug =
    "useRetrieveWithMembershipTicketTypesOverride" in service
      ? service.useRetrieveWithMembershipTicketTypesOverride // public events service
      : service.useRetrieve; // venue events service
  const router = useRouter();
  const { isEmbedded } = useEmbeddedComponent();
  // pass into component later.
  const { seatingItemArray } = useSeatingState();
  const { bestAvailableTypeState, dispatch } = useBestAvailableTypeState(
    initialSelectedTicketTypes
  );

  const eventParams = {
    // if on the seating widget, use widget logic for the purchase platform
    purchase_platform: isEmbedded
      ? getEmbeddedPurchasePlatform()
      : enums.PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_WEB.id,
    payment_type: [
      PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
      PAYMENT_TYPE_OPTIONS_MAP.AFTERPAY.id,
      PAYMENT_TYPE_OPTIONS_MAP.AFFIRM.id,
    ].toString(),
    source_channel: router?.query?.source_channel?.toString(),
    include_unconfigurable_ticket_types: !(
      "useRetrieveWithMembershipTicketTypesOverride" in service
    ), // only pass true for venue events service
  };

  const { data: eventData, isLoading: isLoadingEvent } = useGetWithSlug(
    eventSlug,
    {
      config: {
        params: eventParams,
      },
      options: {
        enabled: Boolean(
          typeof window !== "undefined" &&
            typeof document !== "undefined" &&
            eventSlug &&
            eventParams
        ),
      },
    }
  );

  const filteredEventData = eventData
    ? getEventDataWithVisibleTicketTypes(
        eventData,
        typesOnMap,
        seatingItemArray
      )
    : undefined;

  const handleClose = () => {
    updateBestAvailableQueryParams({
      type: BestAvailableQueryActions.TICKET_TYPES,
      payload: { ticket_types: bestAvailableTypeState },
    });
    onClose();
  };

  const handleQuantityChange = (tt: TicketTypeSerializer, quantity: number) => {
    const { id } = tt;

    dispatch({
      type:
        quantity === 0
          ? BestAvailableTypeActions.REMOVE_TYPE
          : BestAvailableTypeActions.SET_TYPE,
      payload: { id, quantity },
    });
  };

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={handleClose}
      isCentered
      size="2xl"
      headerComponent={<Text>{t("Tickets")}</Text>}
      variant="interactive"
      scrollBehavior="inside"
      drawerContentProps={{
        height: "100dvh",
      }}
      bodyComponent={
        <TicketTypesList
          eventData={filteredEventData}
          onAmountRequested={handleQuantityChange}
          forceQuantityButtons
          isLoading={isLoadingEvent}
          initialSelectedTicketTypes={bestAvailableTypeState}
          withDebounce={false}
          enableTracking={false}
          venueData={venueData}
        />
      }
      footerComponent={
        <Button.Solid
          onClick={handleClose}
          width={{ base: "full", sm: "auto" }}
          data-testid="best-available-ticket-type-selection-dialog-continue-button"
        >
          {t("Continue")}
        </Button.Solid>
      }
    />
  );
}

export type BestAvailableTicketTypeSelectionDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  updateBestAvailableQueryParams: Dispatch<BestAvailableSeatingQueryParamsActionType>;
  eventsService: () => UseVenueEventsService | UsePublicEventService;
  eventSlug: string;
  initialSelectedTicketTypes: Record<string, number>;
  typesOnMap: Array<number>;
  venueData?: PublicVenueSerializer | VenueSerializer | undefined;
};
